/**
 * src/api/insights.js
 *
 * API calls for the Admin tab's Insight Selection panel (see AdminInsightsTab.jsx).
 * Backed by gaussiq-intelligence's /insights/selection routes — intelligence owns the
 * insights_jobs table, so the selection read/write lives with it (this used to be on
 * gaussiq-admin's /admin/settings/insights, which reached across the shared DB).
 * Skills Settings, which stays on gaussiq-admin, lives in src/api/admin.js.
 */

import { authedJson, authedFetch, raiseForStatus } from './http'

const apiGet = path => authedJson('GET', path)
const apiPatch = (path, body) => authedJson('PATCH', path, body)
const apiPost = (path, body) => authedJson('POST', path, body)

function fromApiInsightJob(job) {
  return {
    id: job.jobKey,
    title: job.title,
    description: job.description,
    icon: job.icon,
    accent: job.accent,
    enabled: job.enabled,
    displayOrder: job.displayOrder,
  }
}

/**
 * GET /insights/selection — the insight kinds this tenant may select (every global
 * kind plus any custom kind granted to it), each carrying whether it's currently
 * enabled/ordered on the Control Panel. Also returns the tenant's own
 * environment-stable UUID, for display so an admin can quote it when requesting a
 * custom insight. Admin-only (require_admin) on the backend.
 * @returns {Promise<{jobs: Array<object>, maxSelected: number, tenantUuid: string|null}>}
 */
export async function fetchInsightSettings() {
  const data = await apiGet('/insights/selection')
  return {
    jobs: (data.jobs ?? []).map(fromApiInsightJob),
    maxSelected: data.maxSelected,
    tenantUuid: data.tenantUuid ?? null,
  }
}

/**
 * PATCH /insights/selection — the full desired set of selected insight job keys, in
 * display order (position in this list becomes each insight's display_order). Any
 * currently-enabled job left out of this list has its run history deleted
 * server-side — callers must confirm that with the user (see AdminInsightsTab's
 * warning modal) before calling this.
 * @param {string[]} selectedJobKeys
 */
export async function saveInsightSelection(selectedJobKeys) {
  const data = await apiPatch('/insights/selection', { selectedJobKeys })
  return {
    jobs: (data.jobs ?? []).map(fromApiInsightJob),
    maxSelected: data.maxSelected,
    tenantUuid: data.tenantUuid ?? null,
  }
}

// --- Manual insight run (superadmin "Run now") ------------------------------------
// Runs one SAVED Control Panel selection immediately, through the same launch path
// as the scheduler, without moving its schedule. Superadmin-only on the backend
// (require_superadmin). Fire-and-poll like the reports below: these two back the
// useJobStatus state machine (src/hooks/useJobStatus.js).

/**
 * The latest run of this insight, scheduled or manual.
 * GET /insights/{jobKey}/run/status
 * @param {string} jobKey
 * @returns {Promise<{ status: 'none'|'generating'|'done'|'error', message?: string }>}
 */
export async function fetchInsightRunStatus(jobKey) {
  return apiGet(`/insights/${encodeURIComponent(jobKey)}/run/status`)
}

/**
 * Launch a run of one selected insight now.
 * POST /insights/{jobKey}/run
 *
 * Resolves on 202, not when the run finishes — poll fetchInsightRunStatus() for the
 * outcome. Rejects with `status === 409` when a run is already in flight (the
 * scheduler's included), and 404 when the insight isn't a saved selection.
 * @returns {Promise<{ status: string }>}
 */
export async function launchInsightRun(jobKey) {
  return apiPost(`/insights/${encodeURIComponent(jobKey)}/run`)
}

// --- On-demand insight reports (subject-scoped PDF) -----------------------------
// An on-demand insight kind is triggered for one subject (an employee), runs the
// same agent as a scheduled insight, and lands as a PDF in Reports keyed by that
// subject's name — see gaussiq-intelligence routes/insights.py. The trigger returns
// 202 while the run continues server-side (minutes long); the two GETs drive a
// poll-then-view button, keyed on `subject` rather than a path id. These back the
// useReportJob state machine (src/hooks/useReportJob.js).

// The on-demand insight surfaced as a report button on the team-member detail modal.
export const EXPERTISE_AT_RISK_JOB_KEY = 'expertise-at-risk'

/**
 * Whether this subject's on-demand report exists, is generating, or failed.
 * GET /insights/{jobKey}/report/status?subject=…
 * @param {string} jobKey  — the insight kind (e.g. EXPERTISE_AT_RISK_JOB_KEY)
 * @param {string} subject — the subject the report is about (the employee's name)
 * @returns {Promise<{ status: 'none'|'generating'|'done'|'error', message?: string }>}
 */
export async function fetchInsightReportStatus(jobKey, subject) {
  return apiGet(`/insights/${encodeURIComponent(jobKey)}/report/status?subject=${encodeURIComponent(subject)}`)
}

/**
 * Kick off an on-demand insight report run for one subject.
 * POST /insights/{jobKey}/report  { subject }
 *
 * Fire-and-poll: resolves with 202 as soon as the run is registered, not when it
 * finishes. Switch to polling fetchInsightReportStatus() for the outcome; failures
 * surface there, not here.
 * @returns {Promise<{ status: string, subject: string }>}
 */
export async function launchInsightReport(jobKey, subject) {
  return apiPost(`/insights/${encodeURIComponent(jobKey)}/report`, { subject })
}

/**
 * This subject's generated report PDF, as a Blob.
 * GET /insights/{jobKey}/report?subject=…
 *
 * Returns the raw application/pdf bytes rather than a URL because the endpoint is
 * Bearer-authenticated: a bare window.open() on it sends no Authorization header and
 * gets a 401. A 404 (no report yet) arrives as an Error with `status === 404`.
 * @returns {Promise<Blob>}
 */
export async function fetchInsightReport(jobKey, subject) {
  const res = await authedFetch(
    `/insights/${encodeURIComponent(jobKey)}/report?subject=${encodeURIComponent(subject)}`,
  )
  if (!res.ok) await raiseForStatus(res)
  return res.blob()
}
